import { useCallback, useEffect, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import {
  createApiKey as createReportingApiKey,
  getApiKeys as getReportingApiKeys,
  deleteApiKey as deleteReportingApiKey,
} from "sj-ui-toolkit/src/api/admin/settings";
import { humanDateWithoutTime, randomString } from "sj-ui-toolkit/src/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REPORTING_KEY_LENGTH = 32;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReportingApiKey {
  id: number;
  name: string;
}

interface PlatformToken {
  uuid?: string;
  label?: string;
  createdAt?: string;
}

interface IntegrationToken {
  uuid?: string;
  label?: string;
  createdAt?: string;
  resolvedAt?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const copyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

// ---------------------------------------------------------------------------
// Reporting API Keys Tab
// ---------------------------------------------------------------------------

function ReportingKeys({
  showToast,
}: {
  showToast: (s: "success" | "error", t: string, d: string) => void;
}) {
  const [keys, setKeys] = useState<ReportingApiKey[]>([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReportingApiKeys();
      setKeys(data?.apiKeys ?? []);
    } catch (_e) {
      showToast("error", "Error", "Failed to load Reporting API keys");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const keyValue = randomString(REPORTING_KEY_LENGTH);
      await createReportingApiKey(name.trim(), keyValue);
      setNewKey(keyValue);
      setName("");
      showToast("success", "Success", "Reporting API key created");
      await load();
    } catch {
      showToast("error", "Error", "Failed to create Reporting API key");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    confirmDialog({
      message: "Are you sure you want to delete this Reporting API key?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteReportingApiKey(id);
          showToast("success", "Success", "Key deleted");
          await load();
        } catch {
          showToast("error", "Error", "Failed to delete key");
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block mb-1 font-medium">Key Name</label>
          <InputText
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full"
            placeholder="Enter key name"
          />
        </div>
        <Button
          label="Generate Key"
          onClick={handleGenerate}
          disabled={!name.trim() || submitting}
          loading={submitting}
        />
      </div>

      {newKey && (
        <div className={`${themeClasses.bgElevation2} p-3 rounded`}>
          <p className="body-sm font-medium">
            New API Key (copy it now — it will not be shown again):
          </p>
          <code className="block mt-1 body-sm break-all">{newKey}</code>
          <Button
            label="Copy"
            icon="pi pi-copy"
            className="mt-2"
            text
            onClick={() =>
              copyToClipboard(newKey).then(() => showToast("success", "Copied", "API key copied"))
            }
          />
        </div>
      )}

      <DataTable
        value={keys}
        loading={loading}
        stripedRows
        className="w-full"
        tableClassName="dt-responsive-text"
      >
        <Column field="name" header="Name" />
        <Column
          body={(row: ReportingApiKey) => (
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              onClick={() => handleDelete(row.id)}
              aria-label="Delete key"
            />
          )}
        />
      </DataTable>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform Keys Tab
// ---------------------------------------------------------------------------

function PlatformKeys({
  showToast,
}: {
  showToast: (s: "success" | "error", t: string, d: string) => void;
}) {
  const apiTokensClient = getApiTokensClient();
  const [keys, setKeys] = useState<PlatformToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await apiTokensClient.listApiTokens();
      if (error) throw error;
      setKeys(data?.apiTokens || []);
    } catch {
      showToast("error", "Error", "Failed to load platform keys");
    } finally {
      setLoading(false);
    }
  }, [showToast, apiTokensClient]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const { data, error } = await apiTokensClient.issueApiToken(name.trim(), []);
      if (error) throw error;
      setNewKey(data?.secret ?? "");
      setName("");
      showToast("success", "Success", "Platform key created");
      await load();
    } catch {
      showToast("error", "Error", "Failed to create platform key");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (uuid: string) => {
    confirmDialog({
      message: "Are you sure you want to delete this platform key?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const { error } = await apiTokensClient.revokeApiToken(uuid);
          if (error) throw error;
          showToast("success", "Success", "Key deleted");
          await load();
        } catch {
          showToast("error", "Error", "Failed to delete key");
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block mb-1 font-medium">Key Name</label>
          <InputText
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full"
            placeholder="Enter key name"
          />
        </div>
        <Button
          label="Generate Key"
          onClick={handleGenerate}
          disabled={!name.trim() || submitting}
          loading={submitting}
        />
      </div>

      {newKey && (
        <div className={`${themeClasses.bgElevation2} p-3 rounded`}>
          <p className="body-sm font-medium">
            New Platform Key (copy it now — it will not be shown again):
          </p>
          <code className="block mt-1 body-sm break-all">{newKey}</code>
          <Button
            label="Copy"
            icon="pi pi-copy"
            className="mt-2"
            text
            onClick={() =>
              copyToClipboard(newKey).then(() =>
                showToast("success", "Copied", "Platform key copied")
              )
            }
          />
        </div>
      )}

      <DataTable
        value={keys}
        loading={loading}
        stripedRows
        className="w-full"
        tableClassName="dt-responsive-text"
      >
        <Column field="label" header="Name" />
        <Column
          field="uuid"
          header="UUID"
          body={(row: PlatformToken) => (
            <code className="body-sm">{row.uuid?.substring(0, 12)}…</code>
          )}
        />
        <Column
          field="createdAt"
          header="Created"
          body={(row: PlatformToken) => (row.createdAt ? humanDateWithoutTime(row.createdAt) : "—")}
        />
        <Column
          body={(row: PlatformToken) => (
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              onClick={() => row.uuid && handleDelete(row.uuid)}
              aria-label="Delete key"
            />
          )}
        />
      </DataTable>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CI/CD Tokens Tab
// ---------------------------------------------------------------------------

function CicdTokens({
  showToast,
}: {
  showToast: (s: "success" | "error", t: string, d: string) => void;
}) {
  const apiTokensClient = getApiTokensClient();
  const [tokens, setTokens] = useState<IntegrationToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokenLabel, setTokenLabel] = useState("");
  const [newToken, setNewToken] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await apiTokensClient.listApiTokensWithEntitlements([
        "github_action",
        "guardian",
      ]);
      if (error) throw error;
      setTokens(data?.apiTokens || []);
    } catch {
      showToast("error", "Error", "Failed to load CI/CD tokens");
    } finally {
      setLoading(false);
    }
  }, [showToast, apiTokensClient]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    if (!tokenLabel.trim()) return;
    setSubmitting(true);
    try {
      const { data, error } = await apiTokensClient.issueApiToken(tokenLabel.trim(), ["guardian"]);
      if (error) throw error;
      setNewToken(data?.secret ?? "");
      setTokenLabel("");
      showToast("success", "Success", "CI/CD token created");
      await load();
    } catch {
      showToast("error", "Error", "Failed to create CI/CD token");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (uuid: string) => {
    confirmDialog({
      message: "Are you sure you want to delete this CI/CD token?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const { error } = await apiTokensClient.revokeApiToken(uuid);
          if (error) throw error;
          showToast("success", "Success", "Token deleted");
          await load();
        } catch {
          showToast("error", "Error", "Failed to delete token");
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block mb-1 font-medium">Token Name</label>
          <InputText
            value={tokenLabel}
            onChange={e => setTokenLabel(e.target.value)}
            className="w-full"
            placeholder="e.g. Production GitHub Action"
            maxLength={120}
          />
        </div>
        <Button
          label="Generate Token"
          icon="pi pi-plus"
          onClick={handleGenerate}
          disabled={!tokenLabel.trim() || submitting}
          loading={submitting}
        />
      </div>

      {newToken && (
        <div className={`${themeClasses.bgElevation2} p-3 rounded`}>
          <p className="body-sm font-medium">
            New CI/CD Token (copy it now — it will not be shown again):
          </p>
          <code className="block mt-1 body-sm break-all">{newToken}</code>
          <Button
            label="Copy"
            icon="pi pi-copy"
            className="mt-2"
            text
            onClick={() =>
              copyToClipboard(newToken).then(() => showToast("success", "Copied", "Token copied"))
            }
          />
        </div>
      )}

      <DataTable
        value={tokens}
        loading={loading}
        stripedRows
        className="w-full"
        tableClassName="dt-responsive-text"
      >
        <Column field="label" header="Name" />
        <Column
          field="uuid"
          header="UUID"
          body={(row: IntegrationToken) => (
            <code className="body-sm">{row.uuid?.substring(0, 12)}…</code>
          )}
        />
        <Column
          field="createdAt"
          header="Created"
          body={(row: IntegrationToken) =>
            row.createdAt ? humanDateWithoutTime(row.createdAt) : "—"
          }
        />
        <Column
          body={(row: IntegrationToken) => (
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              onClick={() => row.uuid && handleDelete(row.uuid)}
              aria-label="Delete token"
            />
          )}
        />
      </DataTable>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ApiKeysConnection = () => {
  useBreadcrumb({
    label: "API Keys",
    route: AdminAppRoutes.routes.connections.routes.apiKeys.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);

  const showToast = useCallback(
    (severity: "success" | "error", summary: string, detail: string) => {
      toast.current?.show({ severity, summary, detail, life: 3000 });
    },
    []
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>API Keys</h1>
      </div>
      <Card>
        <TabView>
          <TabPanel header="Reporting API">
            <ReportingKeys showToast={showToast} />
          </TabPanel>
          <TabPanel header="Platform Keys">
            <PlatformKeys showToast={showToast} />
          </TabPanel>
          <TabPanel header="CI/CD Tokens">
            <CicdTokens showToast={showToast} />
          </TabPanel>
        </TabView>
      </Card>
    </>
  );
};

export default ApiKeysConnection;
